import { DetailedHTMLProps, forwardRef, Fragment, InputHTMLAttributes, ReactNode, useId } from 'react';

import { classNames } from 'support/helpers/generic/generic';
import { FieldsetLegend } from 'components/Form/Input/FieldsetLegend/FieldsetLegend';

type VARIANTS = 'normal' | 'list-compact' | 'list';

const RadioGroup = ({
  children,
  label,
  variant = 'normal',
  legendVariant,
  required,
  className,
  description,
}: {
  children: ReactNode;
  label?: string;
  variant?: VARIANTS;
  legendVariant?: 'default' | 'large';
  required?: boolean;
  className?: string;
  description?: ReactNode;
}) => {
  let content: ReactNode = null;

  switch (variant) {
    case 'list-compact':
      content = children;
      break;
    case 'list':
    case 'normal':
    default:
      content = <div className="space-y-3">{children}</div>;
  }

  return (
    <fieldset className={classNames('mt-4', className)}>
      <FieldsetLegend
        label={label}
        description={description}
        variant={legendVariant}
        required={required}
        className="mb-2"
      />
      {content}
    </fieldset>
  );
};

const checkedClasses =
  'peer-has-[:checked]:border-t-0 has-[:checked]:border-b has-[:checked]:border-procuros-green-200 has-[:checked]:bg-procuros-green-50 has-[:checked]:text-procuros-green-700';
const bordersAndColorsClasses = 'text-gray-500 border border-gray-200 first:border-b-0';
const otherClasses = 'peer flex justify-between items-center p-4 first:rounded-t-lg last:rounded-b-lg';

const RadioCompactListItemWrapper = ({ children }: { children: ReactNode }) => {
  return <div className={classNames(checkedClasses, bordersAndColorsClasses, otherClasses)}>{children}</div>;
};

const RadioListItemWrapper = ({ children }: { children: ReactNode }) => {
  return <div className="flex rounded-lg border border-gray-200 bg-white p-3 text-gray-500">{children}</div>;
};

type RadioItemProps = {
  variant?: VARIANTS;
  description?: string;
  label: ReactNode;
  hasErrors?: boolean;
} & Omit<DetailedHTMLProps<InputHTMLAttributes<HTMLInputElement>, HTMLInputElement>, 'type' | 'id' | 'className'>;

const RadioItem = forwardRef<HTMLInputElement, RadioItemProps>(
  ({ description, variant = 'normal', label, children, hasErrors, ...inputProps }, ref) => {
    const id = useId();
    let Wrapper: React.FC<{ children: ReactNode }> = Fragment;
    let containerClassName = 'items-center';
    let labelClassName = 'leading-6';
    let descriptionClassName = '';
    let radioClassName = 'my-1';
    switch (variant) {
      case 'list':
        Wrapper = RadioListItemWrapper;
        break;
      case 'list-compact':
        Wrapper = RadioCompactListItemWrapper;
        break;
      case 'normal':
      default:
        radioClassName = description ? 'my-[2px]' : radioClassName;
        labelClassName = description ? 'leading-5' : labelClassName;
        containerClassName = 'items-start';
        descriptionClassName = 'text-gray-500';
        Wrapper = Fragment;
    }

    return (
      <Wrapper>
        <div className={classNames('flex gap-3', containerClassName)}>
          <input
            {...inputProps}
            ref={ref}
            id={id}
            type="radio"
            className={classNames(
              'h-4 w-4 border-gray-300 hover:bg-gray-50 text-gray-700 checked:hover:text-gray-500 focus:ring-transparent focus-visible:ring-procuros-green-400 focus-visible:text-gray-700 disabled:border-gray-200 disabled:bg-gray-200',
              radioClassName,
              hasErrors ? 'border-red-300 ' : undefined,
            )}
          />
          <div>
            <label htmlFor={id} className={classNames(`block text-sm font-medium text-gray-700`, labelClassName)}>
              {label}
              {Boolean(inputProps.required) && <span className="text-procuros-green-500"> *</span>}
            </label>
            {description && variant !== 'list-compact' && (
              <span className={classNames(`text-sm font-normal leading-5`, descriptionClassName)}>{description}</span>
            )}
          </div>
          {children}
        </div>
        {description && variant === 'list-compact' && (
          <span className="text-sm font-normal leading-5">{description}</span>
        )}
      </Wrapper>
    );
  },
);

RadioItem.displayName = 'RadioItem';

export const Radio = Object.assign({}, { Group: RadioGroup, Item: RadioItem });
